/**
 * Merged LDS proposal — display / navigation UX (read-only).
 */
import { resolveLdsOrderPageDisplayTotalSsot } from "./ldsOrderPageDisplayTotalSsot";
import { resolveLdsOrderDisplayRef } from "../resolveLdsOrderDisplayRef";

export const MERGE_META_KEY = "__hr_merge_meta__";
export const MERGE_SOURCE_KEY = "__hr_merge_source__";
export const MERGED_ORDER_NAVIGATION_TRACE_TAG = "[MERGED_ORDER_NAVIGATION_TRACE]";

function strip(v) {
  return v == null ? "" : String(v).trim();
}

function parseJsonField(raw) {
  if (raw == null || typeof raw === "object") return raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
}

function mergeMetaFromExtended(ext) {
  if (!Array.isArray(ext)) return null;
  return ext.find((b) => b && typeof b === "object" && b[MERGE_META_KEY]) || null;
}

function mergeSourceFromExtended(ext) {
  if (!Array.isArray(ext)) return null;
  return ext.find((b) => b && typeof b === "object" && b[MERGE_SOURCE_KEY]) || null;
}

/** Short HR-style label from internal order UUID. */
export function formatMergedOrderHrLabel(orderId, orderRow = null) {
  const ref = orderRow ? resolveLdsOrderDisplayRef(orderRow) : null;
  const display = ref?.displayValue;
  if (
    display &&
    display !== "ממתין לאישור ספק" &&
    !String(display).includes("-") &&
    String(display).length <= 24
  ) {
    return `HR-${String(display).replace(/\s/g, "")}`;
  }
  const compact = strip(orderId).replace(/-/g, "").slice(0, 8).toUpperCase();
  return compact ? `HR-${compact}` : "HR-????";
}

export function resolveMergedOrderUx(order) {
  const ext = parseJsonField(order?.extended);
  const source = mergeSourceFromExtended(ext);
  const meta = mergeMetaFromExtended(ext);
  const mergedIntoOrderId = strip(source?.merged_into_order_id ?? source?.mergedIntoOrderId);
  const mergedFromOrderIds = Array.isArray(meta?.merged_from_order_ids)
    ? meta.merged_from_order_ids.map((id) => strip(id)).filter(Boolean)
    : [];

  const isMergedSource = Boolean(mergedIntoOrderId);
  const isMergedTarget = Boolean(meta && mergedFromOrderIds.length > 0);

  let badgeLabel = "";
  let badgeTargetOrderId = null;
  let badgeClickable = false;

  if (isMergedSource) {
    badgeTargetOrderId = mergedIntoOrderId;
    badgeLabel = `אוחד להזמנה ${formatMergedOrderHrLabel(mergedIntoOrderId)}`;
    badgeClickable = true;
  } else if (isMergedTarget) {
    badgeLabel = "הוזמנה כחלק מהזמנה מאוחדת";
    badgeClickable = false;
  }

  return {
    isMergedSource,
    isMergedTarget,
    mergedIntoOrderId: mergedIntoOrderId || null,
    mergedFromOrderIds,
    mergedFromCount: mergedFromOrderIds.length,
    mergeGroupId: strip(meta?.merge_group_id ?? meta?.mergeGroupId) || null,
    badgeLabel,
    badgeTargetOrderId,
    badgeClickable,
    sourceBlock: source,
    metaBlock: meta,
  };
}

export function filterByMergeRole(orders, mergeRole = "all") {
  const role = strip(mergeRole) || "all";
  if (!Array.isArray(orders) || role === "all") return orders || [];
  return orders.filter((row) => {
    const ux = resolveMergedOrderUx(row);
    if (role === "merged_only") return ux.isMergedTarget;
    if (role === "source_only") return ux.isMergedSource;
    return true;
  });
}

export function orderMatchesMergeSearchQuery(order, query) {
  const q = strip(query).toLowerCase();
  if (!q) return true;
  const ux = resolveMergedOrderUx(order);
  const id = strip(order?.id).toLowerCase();
  const dealid = strip(order?.dealid ?? order?.deal_id).toLowerCase();
  const ref = resolveLdsOrderDisplayRef(order);
  const displayRef = strip(ref?.displayValue).toLowerCase();
  const bookingRef = strip(order?.booking_ref ?? order?.bookingRef).toLowerCase();
  const mergedInto = strip(ux.mergedIntoOrderId).toLowerCase();
  const mergedIntoHr = ux.mergedIntoOrderId
    ? formatMergedOrderHrLabel(ux.mergedIntoOrderId, order).toLowerCase()
    : "";
  const groupId = strip(ux.mergeGroupId).toLowerCase();
  const haystack = [id, dealid, displayRef, bookingRef, mergedInto, mergedIntoHr, groupId]
    .filter(Boolean)
    .join(" ");
  return haystack.includes(q);
}

export function emitMergedOrderNavigationTrace(order, options = {}) {
  if (typeof console === "undefined" || !console.info) return;
  const ux = resolveMergedOrderUx(order);
  try {
    console.info(MERGED_ORDER_NAVIGATION_TRACE_TAG, {
      orderId: strip(order?.id),
      isMergedSource: ux.isMergedSource,
      isMergedTarget: ux.isMergedTarget,
      mergedIntoOrderId: ux.mergedIntoOrderId,
      mergedFromCount: ux.mergedFromCount,
      ...options,
    });
  } catch {
    /* ignore */
  }
}

export function summarizeMergedSourceOrderRow(order) {
  const pi = parseJsonField(order?.passengersinfo ?? order?.passengers_info);
  let roomCount = 0;
  let paxCount = 0;
  if (Array.isArray(pi)) {
    roomCount = pi.length;
    for (const room of pi) {
      if (!room || typeof room !== "object") continue;
      paxCount +=
        (Array.isArray(room.adults) ? room.adults.length : 0) +
        (Array.isArray(room.kids) ? room.kids.length : 0) +
        (Array.isArray(room.infants) ? room.infants.length : 0);
    }
  }
  const totalSsot = resolveLdsOrderPageDisplayTotalSsot(order, { emitTrace: false });
  const agentLabel = [order?.user_first_name, order?.user_last_name].filter(Boolean).join(" ");
  const hr = formatMergedOrderHrLabel(order?.id, order);
  return {
    orderId: strip(order?.id),
    hrLabel: hr,
    agentLabel: agentLabel || "—",
    totalIls: totalSsot.displayedTotal,
    roomCount,
    paxCount,
  };
}
